import React, { useState, useEffect } from 'react';
import { FaKey, FaEye, FaEyeSlash, FaEnvelope } from 'react-icons/fa';
import { Alert, Button, Modal, Form, Spinner, InputGroup } from 'react-bootstrap';

// Mirrors the server-side @Size(min = 6) rule on AdminResetPasswordRequest.
const MIN_PASSWORD_LENGTH = 6;

/**
 * Admin-initiated password reset for another user. The new password takes effect
 * immediately and the target user is forced to change it at their next login.
 */
const ResetPasswordModal = (props) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [fetchingData, setFetchingData] = useState(false);
  // Email-a-reset-link option (only when the deployment has email enabled).
  const [sendingInvite, setSendingInvite] = useState(false);
  // null = nothing attempted; otherwise { ok: boolean, message: string }.
  const [inviteResult, setInviteResult] = useState(null);
  const email = props.user && props.user.email;

  useEffect(() => {
    if (props.show) {
      setPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setValidationError('');
      setSendingInvite(false);
      setInviteResult(null);
    }
  }, [props.show]);

  const handleSendInvite = async () => {
    setSendingInvite(true);
    setInviteResult(null);
    try {
      await props.handleSendInvite(props.user);
      setInviteResult({ ok: true, message: `Reset link sent to ${email}.` });
    } catch (err) {
      // 422 -> { sent: false, error: "..." } arrives as ApiError with the body attached.
      setInviteResult({
        ok: false,
        message: err?.body?.error || err?.message || 'Failed to send the reset email.'
      });
    }
    setSendingInvite(false);
  };

  const handleConfirm = async () => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
      return;
    }
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match.');
      return;
    }
    setValidationError('');
    setFetchingData(true);
    await props.handleConfirm(props.user, password);
    setFetchingData(false);
  };

  return (
    <Modal onHide={() => props.handleClose()} show={props.show}>
      <Modal.Header closeButton>
        Reset password
      </Modal.Header>
      <Modal.Body>
        { (props.error || validationError) &&
          <Alert variant='danger'>
            { validationError || props.error }
          </Alert>
        }
        { props.emailEnabled && (
          <div className='mb-3'>
            <p>
              Email <strong>{email}</strong> a link so they can choose a new
              password themselves.
            </p>
            <Button
              variant='outline-primary'
              disabled={sendingInvite}
              data-testid='reset-password-send-email'
              onClick={handleSendInvite}
            >
              { sendingInvite ? (
                <Spinner
                  className='mr-2'
                  as='span'
                  animation='border'
                  size='sm'
                  role='status'
                  aria-hidden='true'
                />
              ) : (
                <FaEnvelope className='mr-2' />
              )}
              EMAIL A RESET LINK
            </Button>
            { inviteResult && (
              <Alert
                variant={inviteResult.ok ? 'success' : 'danger'}
                className='mt-2 mb-0'
                data-testid='reset-password-invite-result'
              >
                {inviteResult.message}
              </Alert>
            )}
            <hr />
          </div>
        )}
        <p>
          Set a new password for <strong>{email}</strong>. They will be required
          to choose their own password the next time they log in.
        </p>
        <Form.Group>
          <Form.Label>
            New password
          </Form.Label>
          <InputGroup>
            <Form.Control
              data-testid="reset-password-input"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder='Enter new password'
            />
            <Button
              variant="outline-secondary"
              data-testid="reset-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              className="d-flex align-items-center justify-content-center"
              style={{ maxWidth: '42px', borderStyle: 'none' }}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </Button>
          </InputGroup>
        </Form.Group>
        <Form.Group className="mt-3">
          <Form.Label>
            Confirm new password
          </Form.Label>
          <Form.Control
            data-testid="reset-password-confirm-input"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder='Re-enter new password'
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer className='d-flex justify-content-end'>
        <div>
          <Button
            onClick={() => props.handleClose()}
            variant='outline-secondary'
            className='w-100'
          >
            CANCEL
          </Button>
        </div>
        <div>
          <Button
            disabled={fetchingData}
            data-testid='reset-password-confirm'
            onClick={handleConfirm}
            variant='primary'
            className='w-100'
          >
            { fetchingData ? (
              <Spinner
                className='mr-2'
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
              />
            ) : (
              <FaKey className='mr-2' />
            )}
            RESET PASSWORD
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ResetPasswordModal;
